import * as _ from 'lodash';
import { diagnostics, utils } from '@blend/ui-common';
import history, { getBasePath } from 'login-utils/history';

import { updateAttemptedRoute, updateLoanType } from 'login-actions/globalActions';
import * as deploymentConfig from 'login-utils/deploymentConfig';
import States from 'login-constants/states';
import * as featureFlags from './featureFlags';
import * as globalWrappers from './globalWrappers';
import * as userUtils from './userUtils';
import assert from './assert';

const { cookieUtils } = utils;

export const isAtURL = (url: string) => {
  // This is mostly for test code.
  if (!url) {
    return false;
  }

  assert(_.startsWith(url, '/'), `Must provide a relative path to isAtURL for url: ${url}`);
  return _.startsWith(globalWrappers.getLocation().hash, `#${url}`);
};

const isHashRouter = (basePath: string): boolean => basePath === '#/';

/*
Goes to a state, but retains the query params of the state that it's current in.
*/
export function pushWithContext(path: string) {
  const currentLocation = history.getCurrentLocation();
  history.push({
    pathname: `/${path}`,
    search: currentLocation.search,
  });
}

type FlagEnabledFn = (nextState) => void;

export function goBackIfFlagNotEnabled(flagId: FeatureFlagName): FlagEnabledFn {
  return function redirectIfFlagNotEnabled(nextState) {
    if (!featureFlags.isEnabled(flagId)) {
      const nextURL = nextState.location.pathname;
      diagnostics.breadcrumb(`Not going to ${nextURL} because flag ${flagId} is off.`);
      return history.goBack();
    }
  };
}

export function goBackIfDeploymentConfigNotEnabled(configIds: string[]): FlagEnabledFn {
  return function redirectIfDeploymentConfigNotEnabled(nextState) {
    for (const configId of configIds) {
      if (!_.get(deploymentConfig.get(), configId)) {
        const nextURL = nextState.location.pathname;
        diagnostics.breadcrumb(`Not going to ${nextURL} because deployment config ${configId} is off.`);
        return history.goBack();
      }
    }
  };
}

export function goBackIfAllDeploymentConfigsDisabled(configIds: string[]): FlagEnabledFn {
  return function redirectIfAllDeploymentConfigsDisabled(nextState) {
    const configValues = _.map(configIds, configId => _.get(deploymentConfig.get(), configId));
    const hasAllConfigsDisabled = _.every(configValues, configValue => !configValue);
    const nextURL = nextState.location.pathname;

    if (hasAllConfigsDisabled) {
      diagnostics.breadcrumb(`Not going to ${nextURL} because all of the following configs are disabled: ${configIds}`);
      return history.goBack();
    }
  };
}

export function goBackIfNotWhitelistedEnv(whitelistedEnvs: string[]) {
  return function redirectIfNotWhitelistedEnv(nextState) {
    const nodeEnv = globalWrappers.WindowWrapper.NODE_ENV;
    const nextURL = nextState.location.pathname;
    if (!_.includes(whitelistedEnvs, nodeEnv)) {
      diagnostics.breadcrumb(`Not going to ${nextURL} because it is not allowed for node env ${nodeEnv}`);
      return history.goBack();
    }
  };
}

export function goBackIfLoanTypeNotEnabled(loanType: DM.LoanType): FlagEnabledFn {
  return function redirectIfLoanTypeNotEnabled(nextState) {
    if (!_.includes(deploymentConfig.get().supportedLoanTypes, loanType)) {
      const nextURL = nextState.location.pathname;
      diagnostics.breadcrumb(`Not going to ${nextURL} because loan type ${loanType} is not enabled.`);
      return history.goBack();
    }
  };
}

export function redirectIfNoCurrentUser() {
  if (!userUtils.getCurrentUser()) {
    diagnostics.breadcrumb(
      `Redirecting from ${States.LOAN_HYDRATION} to ${States.LOGIN} because there is no current user`,
    );
    return history.push(States.LOGIN);
  }
}

const goToUrl = (path: string, basePath: string) => {
  diagnostics.breadcrumb(`Redirecting to ${path}`);
  let nextPath;
  if (isHashRouter(basePath)) {
    nextPath = `#${path}`;
  } else {
    // For browser router, strip off the base path so that we don't end up with a double base
    // i.e. /entry/entry/foo
    nextPath = path.replace(new RegExp(`^${basePath}`), '');
  }
  // We need to use history.replaceState here as history's replace implementation
  // causes the browser to refresh unexpectedly when it calls window.location.replace
  return globalWrappers.getWindow().history.replaceState(null, '', nextPath);
};

export function interimSSOOnEnter(nextState) {
  if (!deploymentConfig.isNewToBank()) {
    const nextURL = nextState.location.pathname;
    diagnostics.breadcrumb(`Not going to ${nextURL} because New To Bank is disabled.`);
    return history.goBack();
  }

  if (featureFlags.isEnabled('truncatedEConsentForNTB20190522')) {
    diagnostics.breadcrumb(`Going to Signup flow because New To Bank and truncatedEConsentForNTB20190522 are enabled.`);
    return pushWithContext(States.SIGNUP);
  }
}

const isBaseURL = (location: Location, basePath: string) => {
  return isHashRouter(basePath)
    ? location.href === `${location.origin}/`
    : location.pathname === basePath || location.pathname === `${basePath}/`;
};

const isValidApplicationURL = (location: Location, basePath: string) => {
  const hasBasePath = _.includes(location.href, basePath);

  if (!hasBasePath && location.search !== '') {
    return false;
  }
  if (hasBasePath && location.pathname === '/') {
    return true;
  }
  if (isBaseURL(location, basePath)) {
    return true;
  }
  // For browser router, we just want to check that the base path is there.
  if (!isHashRouter(basePath) && location.pathname.startsWith(basePath)) {
    return true;
  }
  return false;
};

/**
 * Rewrite externally linked hash-based URLs if using browser router.
 */
const rewriteHashPath = (location: Location, basePath: string): string => {
  if (isHashRouter(basePath)) return location.pathname;

  if (location.pathname === '/' && location.hash.startsWith('#/')) {
    // We have some places redirecting with a query coming after the fragment which is not spec compliant.
    const [path] = location.hash.split('?');
    return path.replace('#', 'entry');
  }

  return location.pathname;
};

const getQuery = (location: Location): string => {
  if (location.search) return location.search;

  // For hash router, we currently put the query after the fragment which is not spec compliant.
  const [, query] = location.hash.split('?');
  return query ? `?${query}` : '';
};

export const handleInitialRedirect = () => {
  // Bind because we're going to execute it out of context.
  // We use window.history.replaceState because we want to normalize this *before* we get into any react-router
  // URL handling, and because react-router's hashHistory implementation is actively bad.
  const location = globalWrappers.getLocation();
  const basePath = getBasePath();
  // backend will determine what's the proper redirect route in this case

  // If we are currently doing loan hydration or validation, direct users to the corresponding loading page
  if (cookieUtils.get('oneTapValidating')) {
    return goToUrl(`/${States.ONE_TAP_LOADING}`, basePath);
  } else if (cookieUtils.get('loanHydrating')) {
    return goToUrl(`/${States.LOAN_HYDRATION}`, basePath);
  }

  // In all other cases, let's just try to remap and then react-router will fix it if needed.
  const pathName = rewriteHashPath(location, basePath);
  const query = getQuery(location);
  // Determine if we're in a valid URL to avoid doing any manipulation or rewriting. If we are, stop running.
  // In all other cases, let's just try to remap and then react-router will fix it if needed.
  return !isValidApplicationURL(location, basePath) ? goToUrl(`${pathName}${query}`, basePath) : undefined;
};

export const updateLastAttemptedRoute = (nextState, { dispatch }) => {
  const leafRoute = _.last<RouteState>(nextState.routes);
  const { path, name } = leafRoute!;
  dispatch(updateAttemptedRoute({ path, name }));
};

export const setLoanTypeFromQuery = (nextState, { dispatch }) => {
  const queryLoanType = _.get(nextState.location, 'query.loanType');
  if (queryLoanType) {
    dispatch(updateLoanType(queryLoanType));
  }
};
